import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import {
  Cart24,
  Heart24,
  QrCode24,
  Search24,
  User24,
} from '@workspace/ui/icons';

import { SearchBar } from '~/components/HeaderV3/components/SearchBar';
import { HeaderFavs } from '~/components/MobileHeaderCD/components/HeaderFavs';
import { ModalType } from '~/components/Modal/interface';
import { ModalClientCard } from '~/components/ModalClientCard';
import { useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { formatPrice } from '~/utils/formaters';
import bonusImg from '~/assets/img/mcosmetic/bonus.svg';

import { HeaderCartButton } from './HeaderCartButton';

export const HeaderNavigation = observer(() => {
  const { isMobile } = useBreakpoint();
  const { openModal } = useModal();
  const navigate = useNavigate();

  const [isClientCardOpen, setIsClientCardOpen] = useState(false);

  const favsCount = favoritesStore.favorites.length;
  const { enabled: isLoyaltyEnabled, balances: loyaltyBalances } =
    userStore.mindboxLoyaltyStore;
  const primaryLoyaltyBalance = loyaltyBalances[0];
  const showLoyaltyBalance = isLoyaltyEnabled && primaryLoyaltyBalance;

  const iconSize = isMobile ? 20 : 24;

  const handleCloseClientCard = useCallback(
    () => setIsClientCardOpen(false),
    [],
  );

  const handleClientCard = () => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth);
      return;
    }
    setIsClientCardOpen(true);
  };

  const handleSearch = (e: React.MouseEvent) => {
    if (!isMobile) return;

    e.preventDefault();
    navigate('/search');
  };

  const handleAuth = (e: React.MouseEvent) => {
    if (userStore.isAuthorized) {
      return;
    }
    e.preventDefault();
    openModal(ModalType.Auth, 'header', {
      redirect: '/cabinet',
    });
  };

  return (
    <>
      <SearchBar
        trigger={
          <div className="flex items-center justify-center">
            <Search24 height={iconSize} width={iconSize} />
          </div>
        }
        fixedWidth={600}
        onClick={handleSearch}
      />
      {isMobile ? (
        <>
          <button
            type="button"
            aria-label="Client card"
            onClick={handleClientCard}
            className="flex cursor-pointer items-center justify-center border-none bg-transparent"
          >
            <QrCode24
              height={iconSize}
              width={iconSize}
              className="text-navigation-text-in-button! transition-colors duration-300 group-data-[scrolled=true]:text-navigation-icon-primary!"
            />
          </button>
          <ModalClientCard
            isOpen={isClientCardOpen}
            onClose={handleCloseClientCard}
          />
          {showLoyaltyBalance && (
            <div className="flex h-max items-center gap-1 rounded-full bg-graphic-transparent-white-55 p-px pr-2">
              <img src={bonusImg} alt="bonus" className="size-4" />
              <Typography.Text50Semibold className="text-text-primary">
                {formatPrice(primaryLoyaltyBalance.available)}
              </Typography.Text50Semibold>
            </div>
          )}
        </>
      ) : (
        <>
          <HeaderFavs
            enableCaption={false}
            enableLabel={false}
            icon={
              <>
                <Heart24 />
                {favsCount > 0 && (
                  <Typography.Text200Semibold
                    as="span"
                    className="absolute -top-[10px] -right-[14px] size-6 rounded-full bg-action-brand p-1 text-center text-text-in-button"
                  >
                    {favsCount}
                  </Typography.Text200Semibold>
                )}
              </>
            }
            className="max-h-max text-navigation-text-in-button! transition-colors duration-300 group-data-[scrolled=true]:text-navigation-icon-primary!"
            dropdownClassName="max-w-[480px] right-[128px] left-auto! transform-[translate(0)]! bg-bg-content!"
          />
          <Link
            to="/cabinet"
            onClick={handleAuth}
            data-link="header-cabinet"
            className="text-navigation-text-in-button! transition-colors duration-300 group-data-[scrolled=true]:text-navigation-icon-primary!"
          >
            <User24 />
          </Link>
          <HeaderCartButton>
            <Cart24 />
          </HeaderCartButton>
        </>
      )}
    </>
  );
});
